import type { Optional } from '@festina-life/core'
import cx from 'classnames'
import { type ComponentProps, forwardRef, type ReactNode } from 'react'

import { paperStyles } from '../../libs/paperStyles'
import {
  type PropsWithSafeExtra,
  safeRestProps,
} from '../../libs/safeRestProps'
import { typographyStyles } from '../../structure/typography/styles'
import { rawTextInputStyles } from './rawTextInputStyles'

export type RawTextInputProps = PropsWithSafeExtra<
  'input',
  | 'autoComplete'
  | 'autoFocus'
  | 'className'
  | 'defaultValue'
  | 'disabled'
  | 'id'
  | 'name'
  | 'onBlur'
  | 'onChange'
  | 'onClick'
  | 'onError'
  | 'onFocus'
  | 'onKeyDown'
  | 'placeholder'
  | 'readOnly'
  | 'required',
  {
    isInError?: boolean
    addOnProps?: {
      element: ReactNode
      className?: Optional<string>
    }
    value?: string
    editable?: boolean
    isHidden?: boolean
    type?:
      | 'text'
      | 'number'
      | 'date'
      | 'datetime-local'
      | 'hidden'
      | 'tel'
      | 'file'
    ref?: ComponentProps<'input'>['ref']
  }
>

/** Text input that can be used in Festina Forms to display a text input that follows our styling rules. May or may
 * not be wrapped by a FormInput component to add labels, descriptions and error messages around the input field.
 */
export const RawTextInput = forwardRef<HTMLInputElement, RawTextInputProps>(
  (
    {
      isInError = false,
      addOnProps,
      disabled = false,
      editable = true,
      type = 'text',
      value,
      className,
      isHidden,
      onFocus,
      extraProps = {},
      ...restProps // Needed since we can pass other props like aria-*, data-* etc. or spread that isn't type checked
    },
    ref,
  ) => (
    <div className="relative w-full flex">
      {addOnProps && (
        <span
          className={cx(
            'flex items-center rounded-l-lg px-2 -me-px',
            paperStyles({ ringColor: 'grey', isFaded: true }),
            typographyStyles,
            addOnProps.className,
            'focus-within:z-1', // to ensure ring is behind the input field unless the addon is focused
          )}
        >
          {addOnProps.element}
        </span>
      )}

      {editable ? (
        <input
          {...safeRestProps(extraProps, restProps)}
          className={cx(
            rawTextInputStyles({
              isInError,
              isAfterAddOn: !!addOnProps,
              disabled,
            }),
            className,
          )}
          data-autofocus
          onFocus={(event) => {
            if (onFocus) {
              onFocus(event)
            } else {
              event.target.select()
            }
          }}
          size={(value?.toString().length ?? 0) + 1} // Without this inputs have size 20 as default dictating a too wide min width — rawTextInputStyles sets w-full so it fills container.
          aria-invalid={isInError}
          disabled={disabled}
          ref={ref}
          value={value ?? ''} // Fallback to '' makes sure the inputs are controlled always
          type={type}
        />
      ) : (
        <div className="w-full px-2 py-1.5">{value}</div>
      )}
    </div>
  ),
)

RawTextInput.displayName = 'RawTextInput'
